import { AddOnPriceTier } from './types';

/*
 * Whether `quantity` falls inside this band. An absent `max_quantity` is the open-ended top band.
 * Exported because `resolveTier` can return a band that does NOT contain the quantity, so a caller
 * naming the range has to ask -- and a second copy of the rule is a second rule to keep in step.
 */
export const tierCoversQuantity = (tier: AddOnPriceTier, quantity: number): boolean =>
  quantity >= tier.min_quantity && (tier.max_quantity == null || quantity <= tier.max_quantity);

const orderedBands = (tiers: Array<AddOnPriceTier>): Array<AddOnPriceTier> =>
  [...tiers].sort((left, right) => left.min_quantity - right.min_quantity);

/**
 * The band that prices `quantity`, or the first band when none covers it.
 *
 * Flat, not graduated: the band prices every unit, so a line total is `quantity x this`. Do not
 * accumulate across bands -- room pricing is the opposite arithmetic and shares the word. Mirrors
 * helix's `resolveBandPriceCents`, which mirrors what work-orders invoice on.
 *
 * No currency argument, deliberately: helix has already filtered `price_tiers` to `AddOn.currency`.
 */
export const resolveTier = (tiers: Array<AddOnPriceTier> | undefined, quantity: number): AddOnPriceTier | null => {
  if (!tiers?.length || !Number.isFinite(quantity)) {
    return null;
  }

  const ordered = orderedBands(tiers);

  /* First band for an uncovered quantity, matching what work-orders charge: never under-quote. */
  return ordered.find(tier => tierCoversQuantity(tier, quantity)) ?? ordered[0] ?? null;
};

/**
 * The unit price at `quantity`. Null when it cannot price, so a caller falls back to the scalar
 * `price` -- `0` is a real price for a free product.
 *
 * ⛔ Not `resolveTier(...)?.price_cents`. The fallback is per FIELD, not per band: a matched band
 * carrying no price falls through to the first band's price, which is what helix's
 * `resolveBandPriceCents` and work-orders both do. Collapsing it to the band would make this app
 * quote a number the invoice does not use -- the exact split this ticket exists to close.
 */
export const resolveTierPriceCents = (tiers: Array<AddOnPriceTier> | undefined, quantity: number): number | null => {
  if (!tiers?.length || !Number.isFinite(quantity)) {
    return null;
  }

  const ordered = orderedBands(tiers);
  const matched = ordered.find(tier => tierCoversQuantity(tier, quantity));

  return matched?.price_cents ?? ordered[0].price_cents ?? null;
};

/**
 * The unit price to display for an add-on at a given selected quantity.
 *
 * `Math.max(quantity, minQuantity)` rather than the raw quantity: an unselected row has quantity 0
 * and the first band starts at 1, so an unclamped resolve matches no band and the row renders blank.
 * Flooring on the product's configured minimum is work-orders' documented consumer contract, and it
 * means the tile shows the price of what the tenant's first tap would order.
 */
export const displayUnitPriceCents = (
  addOn: { price: number; price_tiers?: Array<AddOnPriceTier> },
  quantity: number,
  minQuantity = 1,
): number => resolveTierPriceCents(addOn.price_tiers, Math.max(quantity, minQuantity, 1)) ?? addOn.price;
